import {useEffect,useRef,useState,type FormEvent} from 'react';
import {LockKeyhole,MessageCircle,Smartphone,X} from 'lucide-react';
import {ShieldCheck} from './Icons';
import {AuthStarField} from './account/authStarField';
import {describeAuthError,isAbortError,tokenStore} from './account/api';
import {isValidChinaMobile,useSession} from './account/session';
import {bindWechatPhone,exchangeWechatLogin,generateClientNonce,getWechatTransactionStatus,sendWechatPhoneCode,startWechatTransaction,type WechatTransaction} from './account/wechat';

/** 登录 / 注册:手机号验证码(新号自动注册)、手机号密码、微信扫码,接现有官网同一套账号服务。
 *  视觉沿用现有官网登录页:星空底 + 玻璃面板 + 测量角标 + 微信图标入口与协议确认框。 */
const CODE_COOLDOWN_SECONDS=60;
function wait(ms:number):Promise<void>{return new Promise(resolve=>window.setTimeout(resolve,ms))}

export function AuthDialog({open,onClose}:{open:boolean;onClose:()=>void}){
 const {status,sendCode,loginWithCode,loginWithPassword,acceptLogin}=useSession();
 const dialog=useRef<HTMLDialogElement>(null),phoneInput=useRef<HTMLInputElement>(null);
 const [method,setMethod]=useState<'code'|'password'>('code'),[phone,setPhone]=useState(''),[credential,setCredential]=useState(''),[agreed,setAgreed]=useState(false),[showPassword,setShowPassword]=useState(false),[error,setError]=useState(''),[notice,setNotice]=useState(''),[busy,setBusy]=useState(false),[cooldown,setCooldown]=useState(0);
 // 微信扫码:弹窗授权 + 轮询;首次登录需要补绑手机号
 const [wechatBusy,setWechatBusy]=useState(false),[binding,setBinding]=useState<{ticket:string;nonce:string;transaction:WechatTransaction}|null>(null);
 // 未勾选协议时点微信图标:先弹协议确认框,同意后在同一次点击里发起授权
 const [consentOpen,setConsentOpen]=useState(false);
 const consentPrimary=useRef<HTMLButtonElement>(null);
 const pollRun=useRef(0);
 const requests=useRef<AbortController|null>(null);
 const wechatPopup=useRef<Window|null>(null);

 useEffect(()=>{
  requests.current=new AbortController();
  setBusy(false);
  if(open){setError('');setNotice('');setCredential('');dialog.current?.showModal();phoneInput.current?.focus()}
  else{dialog.current?.close();setCredential('');setShowPassword(false);setBinding(null);setWechatBusy(false);setConsentOpen(false)}
  return()=>{requests.current?.abort();pollRun.current+=1;wechatPopup.current?.close();wechatPopup.current=null};
 },[open]);
 useEffect(()=>{if(cooldown<=0)return;const timer=window.setTimeout(()=>setCooldown(s=>Math.max(0,s-1)),1000);return()=>window.clearTimeout(timer)},[cooldown]);
 useEffect(()=>{if(consentOpen)consentPrimary.current?.focus()},[consentOpen]);
 // 登录成功后关闭弹窗
 useEffect(()=>{if(open&&status==='authenticated')onClose()},[open,status,onClose]);

 const switchMethod=(next:'code'|'password')=>{setMethod(next);setCredential('');setError('');setNotice('');setShowPassword(false)};
 const validatePhone=()=>{if(!isValidChinaMobile(phone.trim())){setError('请输入正确的中国大陆手机号');phoneInput.current?.focus();return false}return true};
 const fail=(err:unknown,action:'code'|'password'='code')=>{const described=describeAuthError(err,action);setError(described.message);if(described.retryAfterSeconds)setCooldown(described.retryAfterSeconds)};

 const requestCode=async()=>{
  if(busy||cooldown>0)return;
  setError('');setNotice('');
  if(!validatePhone())return;
  setBusy(true);
  const signal=requests.current?.signal;
  try{
   if(binding)await sendWechatPhoneCode(binding.ticket,binding.nonce,phone.trim(),signal);
   else await sendCode(phone.trim(),signal);
   if(signal?.aborted)return;
   setCooldown(CODE_COOLDOWN_SECONDS);
   setNotice('验证码已发送，请查收短信。');
  }catch(err){if(!signal?.aborted&&!isAbortError(err))fail(err)}
  finally{if(!signal?.aborted)setBusy(false)}
 };

 const submit=async(e:FormEvent)=>{
  e.preventDefault();
  if(busy)return;
  setError('');setNotice('');
  if(!validatePhone())return;
  const useCode=method==='code'||binding!==null;
  if(useCode&&!/^\d{6}$/.test(credential)){setError('请输入 6 位短信验证码');return}
  if(!useCode&&!credential){setError('请输入密码');return}
  if(!agreed){setError('请先阅读并同意用户协议与隐私政策');return}
  setBusy(true);
  const signal=requests.current?.signal;
  const version=tokenStore.getSessionVersion();
  try{
   if(binding){
    const bound=await bindWechatPhone(binding.ticket,binding.nonce,phone.trim(),credential,signal);
    const result=await exchangeWechatLogin(binding.transaction.transaction_id,bound.exchange_ticket,binding.nonce,signal);
    if(!signal?.aborted)acceptLogin(result,version);
    return;
   }
   if(method==='password')await loginWithPassword(phone.trim(),credential,signal);
   else await loginWithCode(phone.trim(),credential,signal);
  }catch(err){if(!signal?.aborted&&!isAbortError(err))fail(err,method==='password'&&!binding?'password':'code')}
  finally{if(!signal?.aborted)setBusy(false)}
 };

 const pollWechat=async(transaction:WechatTransaction,nonce:string,run:number,popup:Window,signal:AbortSignal,version:number)=>{
  // 用户关掉授权窗口后,允许回调再迟到几个轮询周期,之后静默复位
  let closedGrace=3;
  while(run===pollRun.current&&!signal.aborted){
   if(Date.parse(transaction.expires_at)<=Date.now()){popup.close();setWechatBusy(false);setError('微信授权已过期，请重新发起');return}
   let state;
   try{state=await getWechatTransactionStatus(transaction,nonce,signal)}
   catch(err){if(run!==pollRun.current||signal.aborted)return;fail(err);await wait(1200);continue}
   if(run!==pollRun.current||signal.aborted)return;
   setError('');
   if(state.status==='pending'){
    if(popup?.closed){closedGrace-=1;if(closedGrace<=0){setWechatBusy(false);return}}
    await wait(Math.min(3000,Math.max(600,state.next_poll_after_ms)));
    continue;
   }
   if(state.status==='phone_binding_required'&&state.binding_ticket){
    popup?.close();
    setBinding({ticket:state.binding_ticket,nonce,transaction});
    setMethod('code');setCredential('');setCooldown(0);
    setNotice('微信已授权，绑定手机号后完成登录。');
    setWechatBusy(false);
    return;
   }
   if(state.status==='login_ready'&&state.exchange_ticket){
    popup?.close();
    const result=await exchangeWechatLogin(transaction.transaction_id,state.exchange_ticket,nonce,signal);
    if(run===pollRun.current&&!signal.aborted)acceptLogin(result,version);
    return;
   }
   popup?.close();
   setWechatBusy(false);
   setError(state.status==='expired'?'微信授权已过期，请重新发起':state.error||'微信授权未完成，请重新尝试');
   return;
  }
 };

 /** 协议已同意才会走到这里:弹窗必须在点击的同步调用栈里先开,否则会被浏览器拦截。 */
 const startWechat=async()=>{
  if(wechatBusy)return;
  setError('');setNotice('');
  const popup=window.open('about:blank','sciland-wechat-login','popup,width=520,height=720');
  if(!popup){setError('微信授权窗口被浏览器拦截，请允许弹出窗口后重试');return}
  popup.opener=null;
  wechatPopup.current=popup;
  const signal=requests.current!.signal;
  const version=tokenStore.getSessionVersion();
  setWechatBusy(true);
  const run=++pollRun.current;
  try{
   const nonce=generateClientNonce();
   const transaction=await startWechatTransaction(nonce,signal);
   if(run!==pollRun.current||signal.aborted){popup.close();return}
   popup.location.replace(transaction.auth_url);
   await pollWechat(transaction,nonce,run,popup,signal,version);
  }catch(err){
   popup?.close();
   if(run===pollRun.current&&!signal.aborted){if(!isAbortError(err))fail(err);setWechatBusy(false)}
  }
 };
 const clickWechat=()=>{if(wechatBusy)return;if(!agreed){setConsentOpen(true);return}void startWechat()};
 const consentAndStart=()=>{setAgreed(true);setConsentOpen(false);void startWechat()};
 const cancelBinding=()=>{pollRun.current+=1;setBinding(null);setCredential('');setCooldown(0);setError('');setNotice('')};

 const codeField=method==='code'||binding!==null;
 const phoneInvalid=error.includes('手机号');
 const secretInvalid=error.includes('验证码')||error==='请输入密码';
 return <dialog ref={dialog} className="auth-dialog" aria-labelledby="auth-title" onCancel={e=>{if(consentOpen){e.preventDefault();setConsentOpen(false);return}onClose()}} onClose={onClose}>
 <div className="auth-sky" aria-hidden="true"><AuthStarField/><span className="auth-glow"/></div>
 <button className="auth-close" type="button" aria-label="关闭登录窗口" onClick={onClose}><X aria-hidden="true" size={18}/></button>
 <div className="auth-scroll">
  <div className="auth-glass auth-panel">
   <span className="auth-corners" aria-hidden="true"><i/><i/><i/><i/></span>
   <div className="auth-brand"><img src="/sciland-icon.png" alt="" aria-hidden="true" width="32" height="32"/><p className="auth-kicker">Account</p></div>
   <h2 id="auth-title">{binding?'绑定手机号后继续':'登录 / 注册'}</h2>
   <p className="auth-lede">{binding?'这是该微信首次登录，请验证手机号以创建或关联账号。':'欢迎回来——继续你的研究。新手机号通过验证码登录时自动注册。'}</p>

   {!binding&&<div className="auth-tabs" role="group" aria-label="登录方式">
    <button type="button" disabled={busy||wechatBusy} aria-pressed={method==='code'} onClick={()=>switchMethod('code')}><Smartphone aria-hidden="true" size={16}/>验证码登录</button>
    <button type="button" disabled={busy||wechatBusy} aria-pressed={method==='password'} onClick={()=>switchMethod('password')}><LockKeyhole aria-hidden="true" size={16}/>密码登录</button>
   </div>}

   <form className="auth-form" onSubmit={submit} noValidate>
    <div className="auth-field">
     <label htmlFor="auth-phone">手机号</label>
     <input ref={phoneInput} id="auth-phone" aria-invalid={phoneInvalid||undefined} aria-describedby={phoneInvalid?'auth-feedback':undefined} type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={11} value={phone} onChange={e=>{setPhone(e.target.value.replace(/\D/g,'').slice(0,11));setError('')}} placeholder="请输入手机号" required/>
    </div>
    <div className="auth-field">
     <label htmlFor="auth-credential">{codeField?'验证码':'密码'}</label>
     <div className="auth-field-row">
      <input id="auth-credential" aria-invalid={secretInvalid||undefined} aria-describedby={secretInvalid?'auth-feedback':undefined} type={!codeField&&!showPassword?'password':'text'} inputMode={codeField?'numeric':undefined} autoComplete={codeField?'one-time-code':'current-password'} maxLength={codeField?6:128} value={credential} onChange={e=>{setCredential(codeField?e.target.value.replace(/\D/g,''):e.target.value);setError('')}} placeholder={codeField?'6 位验证码':'请输入登录密码'} required/>
      {codeField
       ?<button className="auth-ghost" type="button" disabled={busy||wechatBusy||cooldown>0} onClick={()=>void requestCode()}>{cooldown>0?`${cooldown} 秒后重发`:'获取验证码'}</button>
       :<button className="auth-ghost" type="button" aria-pressed={showPassword} onClick={()=>setShowPassword(!showPassword)}>{showPassword?'隐藏密码':'显示密码'}</button>}
     </div>
    </div>
    {method==='password'&&!binding&&<button className="auth-recovery" type="button" onClick={()=>{switchMethod('code');setNotice('可以用手机号验证码登录。')}}>忘记密码？使用验证码登录</button>}

    <label className="auth-agree" htmlFor="auth-agree">
     <input id="auth-agree" type="checkbox" checked={agreed} onChange={e=>{setAgreed(e.target.checked);if(e.target.checked)setError('')}}/>
     <span>我已阅读并同意 <a href="/terms" target="_blank" rel="noopener">《用户协议》<span className="sr-only">（新窗口打开）</span></a> 与 <a href="/privacy" target="_blank" rel="noopener">《隐私政策》<span className="sr-only">（新窗口打开）</span></a></span>
    </label>

    <div id="auth-feedback" className="auth-feedback">
     {error?<p key="error" className="auth-error" role="alert">{error}</p>:notice?<p key="notice" className="auth-notice" role="status">{notice}</p>:null}
    </div>

    <button className="auth-primary" type="submit" disabled={busy||wechatBusy}>{busy?'登录中…':binding?'绑定并登录':method==='code'?'登录 / 注册':'登录'}</button>
    {binding&&<button className="auth-outline" type="button" disabled={busy} onClick={cancelBinding}>返回其他登录方式</button>}
   </form>

   {!binding&&<div className="auth-other">
    <div className="auth-rule" aria-hidden="true"><span/><small>其他登录方式</small><span/></div>
    <button className="auth-wechat" type="button" aria-label="微信登录" title="微信登录" disabled={busy||wechatBusy} onClick={clickWechat}><MessageCircle aria-hidden="true" size={20}/></button>
    {wechatBusy
     ?<p className="auth-waiting" role="status"><span className="auth-dot" aria-hidden="true"/>等待微信授权…</p>
     :<p className="auth-other-label">微信登录</p>}
   </div>}

   <p className="auth-note"><ShieldCheck aria-hidden="true" size={14}/>登录态仅保存在本次浏览器会话中</p>
  </div>
 </div>

 {consentOpen&&<div className="auth-consent-layer" role="dialog" aria-modal="true" aria-labelledby="auth-consent-title">
  <button className="auth-consent-scrim" type="button" tabIndex={-1} aria-label="关闭协议确认" onClick={()=>setConsentOpen(false)}/>
  <div className="auth-glass auth-consent">
   <span className="auth-corners" aria-hidden="true"><i/><i/><i/><i/></span>
   <h2 id="auth-consent-title">服务协议与隐私保护</h2>
   <p>使用微信登录前，请阅读并同意 <a href="/terms" target="_blank" rel="noopener">《用户协议》</a> 与 <a href="/privacy" target="_blank" rel="noopener">《隐私政策》</a>。未注册的微信授权后将自动创建账号。</p>
   <div className="auth-consent-actions">
    <button ref={consentPrimary} className="auth-primary" type="button" onClick={consentAndStart}>同意并继续</button>
    <button className="auth-outline" type="button" onClick={()=>setConsentOpen(false)}>不同意</button>
   </div>
  </div>
 </div>}
 </dialog>
}
